import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  CheckCircle,
  ArrowRight,
  Star,
  Award,
  UserCheck,
  FileCheck,
  MonitorSmartphone,
  Sparkles,
  Wrench,
  Shield,
  Settings,
  Users
} from 'lucide-react';
import {
  mockServices,
  mockTestimonials,
  mockClients,
  mockWhyChooseUs,
  mockSubscriptionPlans,
  submitNewsletter
} from '../mock';

const iconMap = {
  Award: Award,
  UserCheck: UserCheck,
  FileCheck: FileCheck,
  MonitorSmartphone: MonitorSmartphone,
  Sparkles: Sparkles,
  Wrench: Wrench,
  Shield: Shield,
  Settings: Settings,
  Users: Users
};

const HomePage = () => {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) {
      toast.error('Please enter your email address');
      return;
    }
    setIsSubmitting(true);
    const result = await submitNewsletter(newsletterEmail);
    setIsSubmitting(false);
    if (result.success) {
      toast.success(result.message);
      setNewsletterEmail('');
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-blue-50 opacity-70"></div>

        <div className="container mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 animate-fade-in">
              <Badge className="bg-[#1C4BB6]/10 text-[#1C4BB6] hover:bg-[#1C4BB6]/20 border-0">
                Professional Facility Management
              </Badge>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Integrated Facility & Staffing Solutions
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                Reliable. Compliant. Performance-Driven. Professional facility management and manpower solutions tailored to meet your operational needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link to="/contact">
                  <Button className="bg-[#1C4BB6] hover:bg-[#154096] text-white px-8 py-6 text-lg transition-all duration-300 hover:shadow-lg hover:scale-105">
                    Get Started <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button variant="outline" className="border-[#1C4BB6] text-[#1C4BB6] hover:bg-[#1C4BB6] hover:text-white px-8 py-6 text-lg transition-all duration-300">
                    Our Services
                  </Button>
                </Link>
              </div>
              <div className="flex items-center gap-8 pt-8">
                {/*} <div>
                  <p className="text-3xl font-bold text-[#1C4BB6]">10+</p>
                  <p className="text-sm text-gray-600">Years Experience</p>
                </div>
                <div className="h-12 w-px bg-gray-300"></div>
                <div>
                  <p className="text-3xl font-bold text-[#1C4BB6]">500+</p>
                  <p className="text-sm text-gray-600">Projects Completed</p>
                </div>
                <div className="h-12 w-px bg-gray-300"></div>
                <div>
                  <p className="text-3xl font-bold text-[#1C4BB6]">100+</p>
                  <p className="text-sm text-gray-600">Happy Clients</p>
                </div>*/}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1708192548842-2d8babc7ddd3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxmYWNpbGl0eSUyMG1hbmFnZW1lbnQlMjBjb3Jwb3JhdGV8ZW58MHx8fHwxNzcwOTA5MTg3fDA&ixlib=rb-4.1.0&q=85"
                alt="Facility Management"
                className="rounded-2xl shadow-2xl w-full h-auto"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="bg-green-100 p-3 rounded-full">
                    <CheckCircle className="text-green-600" size={24} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Certified & Compliant</p>
                    <p className="text-sm text-gray-600">ISO Certified Services</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-[#1C4BB6]/10 text-[#1C4BB6] mb-4">Our Services</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Comprehensive Facility Solutions</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We combine manpower excellence with structured facility systems to deliver measurable operational performance.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockServices.slice(0, 6).map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Card key={service.id} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group">
                  <CardContent className="p-6">
                    <div className="bg-[#1C4BB6]/10 w-16 h-16 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#1C4BB6] transition-colors duration-300">
                      <Icon className="text-[#1C4BB6] group-hover:text-white transition-colors duration-300" size={32} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600 mb-4">{service.description}</p>
                    <Link to="/services" className="text-[#1C4BB6] font-semibold flex items-center hover:gap-2 transition-all">
                      Learn More <ArrowRight size={16} className="ml-1" />
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button className="bg-[#1C4BB6] hover:bg-[#154096] text-white px-8 py-6 transition-all duration-300 hover:shadow-lg">
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-[#1C4BB6]/10 text-[#1C4BB6] mb-4">Why Choose Us</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Excellence in Every Detail</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Professional ethics, trained workforce, and structured processes form the foundation of our service delivery.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {mockWhyChooseUs.map((item) => {
              const Icon = iconMap[item.icon];
              return (
                <div key={item.id} className="text-center p-6 hover:bg-white rounded-xl transition-all duration-300">
                  <div className="bg-[#1C4BB6] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="text-white" size={28} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-[#1C4BB6]/10 text-[#1C4BB6] mb-4">Our Clients</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Trusted by Leading Organizations</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We serve industrial units, corporate offices, healthcare centers, educational institutions, and residential complexes.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-8">
            {mockClients.map((client) => (
              <div
                key={client.id}
                className="flex items-center justify-center p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all"
              >
                <img
                  src={client.logo}
                  alt={client.name}
                  className="h-16 object-contain grayscale hover:grayscale-0 transition"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 bg-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1615914143778-1a1a6e50c5dd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMG9mZmljZSUyMG1lZXRpbmd8ZW58MHx8fHwxNzcwOTA5MjIxfDA&ixlib=rb-4.1.0&q=85"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="container mx-auto relative z-10">
          <div className="text-center mb-16">
            <Badge className="bg-white/10 text-white mb-4 border-0">Testimonials</Badge>
            <h2 className="text-4xl font-bold mb-4">What Our Clients Say</h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Building long-term partnerships through trust, accountability, and consistent service quality.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {mockTestimonials.map((testimonial) => (
              <Card key={testimonial.id} className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20 transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="text-yellow-400 fill-current" size={20} />
                    ))}
                  </div>
                  <p className="text-white mb-6 italic">"{testimonial.content}"</p>
                  <div>
                    <p className="font-semibold text-white">{testimonial.name}</p>

                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Subscription Plans Section */}
      {/*}  <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-[#1C4BB6]/10 text-[#1C4BB6] mb-4">Service Plans</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Choose Your Service Plan</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Flexible solutions designed to meet your specific facility management needs.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {mockSubscriptionPlans.map((plan) => (
              <Card key={plan.id} className={`relative ${plan.popular ? 'border-[#1C4BB6] border-2 shadow-xl' : 'border-gray-200'} hover:shadow-2xl transition-all duration-300`}>
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-[#1C4BB6] text-white px-4 py-1">Most Popular</Badge>
                  </div>
                )}
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <p className="text-gray-600 mb-6">{plan.description}</p>
                  <div className="mb-6">
                    <p className="text-3xl font-bold text-[#1C4BB6]">{plan.price}</p>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle className="text-green-500 mr-2 flex-shrink-0 mt-0.5" size={18} />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact">
                    <Button className={`w-full ${plan.popular ? 'bg-[#1C4BB6] hover:bg-[#154096] text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'} transition-all duration-300`}>
                      Contact Us
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>*/}

      {/* Newsletter Section */}
      <section className="py-20 px-4 bg-[#1C4BB6]">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Stay Updated</h2>
          <p className="text-blue-100 mb-8">
            Subscribe to our newsletter for the latest updates on facility management trends and insights.
          </p>
          <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
            <Input
              type="email"
              placeholder="Enter your email"
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              className="flex-1 bg-white"
            />
            <Button type="submit" disabled={isSubmitting} className="bg-white text-[#1C4BB6] hover:bg-gray-100 px-8">
              {isSubmitting ? 'Subscribing...' : 'Subscribe'}
            </Button>
          </form>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="container mx-auto text-center max-w-4xl">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Ready to Get Started?</h2>
          <p className="text-xl text-gray-600 mb-8">
            Let's build operational excellence together. Contact us today for a consultation.
          </p>
          <Link to="/contact">
            <Button className="bg-[#1C4BB6] hover:bg-[#154096] text-white px-12 py-6 text-lg transition-all duration-300 hover:shadow-lg hover:scale-105">
              Contact Us Today <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
